import { useCallback, useEffect, useMemo, useState } from 'react'
import { Compteur } from '../Compteur'
import {
  caAnnualise,
  chargerMouvements,
  chargerPlan,
  controlesCompta,
  evenements,
  grandLivre,
  indicateurs,
  journal,
  parMois,
  prefixesActifs,
  regimeFiscal,
  valeursOhada,
  MOUVEMENTS_VIDES,
  type AlerteCompta,
  type EvenementComptable,
  type Mouvements,
  type GrandLivre,
  type Indicateurs,
  type LigneJournal,
  type MoisComptable,
  type PlanComptable,
} from '../../api/compta'
import { BILAN_ACTIF, BILAN_PASSIF, RESULTAT, type PosteOhada } from '../../api/ohada'
import type { Commande } from '../../api/types'
import { fcfa } from '../Format'
import { Icone } from '../Icone'
import { ListeDeroulante } from '../ListeDeroulante'
import { Modale } from '../Modale'
import { Anneau, GraphiqueLignes, LegendeLignes } from '../Graphiques'
import { BilanActif, EcrituresManuelles, Flux, PlanComptableVue, TypesEcritures } from './ComptaAnnexes'

type Vue = 'bord' | 'journal' | 'balance' | 'resultat' | 'bilan' | 'flux' | 'comptes' | 'types' | 'manuel'

/**
 * ⚠️ NEUF sous-onglets, comme l'original — le portage n'en avait que cinq.
 * Manquaient le tableau de FLUX (troisième état de synthèse exigé par
 * l'OHADA, à côté du bilan et du résultat), le plan comptable, les types
 * d'écritures et les saisies manuelles. Sans ces dernières, aucune charge
 * hors commandes — assurance, hébergement, marketing — n'entre au résultat.
 */
const VUES: Array<{ cle: Vue; libelle: string; icone: string }> = [
  { cle: 'bord', libelle: 'Tableau de bord', icone: 'grille' },
  { cle: 'journal', libelle: 'Journal', icone: 'book' },
  { cle: 'balance', libelle: 'Balance', icone: 'balance' },
  { cle: 'resultat', libelle: 'Compte de résultat', icone: 'courbe' },
  { cle: 'bilan', libelle: 'Bilan', icone: 'bilan' },
  { cle: 'flux', libelle: 'Flux de trésorerie', icone: 'flux' },
  { cle: 'comptes', libelle: 'Plan comptable', icone: 'plan' },
  { cle: 'types', libelle: "Types d'écritures", icone: 'reglages' },
  { cle: 'manuel', libelle: 'Saisies manuelles', icone: 'edit' },
]

export function EcranCompta({ commandes }: { commandes: Commande[] }) {
  const [plan, setPlan] = useState<PlanComptable | null>(null)
  const [mvt, setMvt] = useState<Mouvements>(MOUVEMENTS_VIDES)
  const [erreurMvt, setErreurMvt] = useState<string | null>(null)
  const [vue, setVue] = useState<Vue>('bord')
  const [erreur, setErreur] = useState<string | null>(null)

  const recharger = useCallback(async () => {
    try {
      /* Les mouvements réels (paiements, paies, reversements) ne bloquent pas
         l'écran s'ils sont illisibles : le journal s'affiche sans eux, et une
         alerte dit que la trésorerie n'est alors pas fiable. */
      const [p, m] = await Promise.all([
        chargerPlan(),
        chargerMouvements().then(
          (r) => ({ ok: true as const, r }),
          (e: unknown) => ({ ok: false as const, e }),
        ),
      ])
      setPlan(p)
      if (m.ok) {
        setMvt(m.r)
        setErreurMvt(null)
      } else {
        setMvt(MOUVEMENTS_VIDES)
        setErreurMvt(m.e instanceof Error ? m.e.message : String(m.e))
      }
      setErreur(null)
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Plan comptable illisible.')
    }
  }, [])

  useEffect(() => {
    void recharger()
  }, [recharger])

  /* Tout le calcul dérive de deux entrées : les commandes et le plan. Aucun
     état intermédiaire n'est stocké — c'est ce qui garantit que le journal, la
     balance et les états ne peuvent pas diverger entre eux. */
  const compta = useMemo<Compta | null>(() => {
    if (!plan) return null
    const ev = evenements(commandes, plan, plan.tva, mvt)
    const lignes = journal(ev, plan)
    const gl = grandLivre(lignes, plan)
    const kpi = indicateurs(commandes, ev, gl, plan)
    const prefixes = prefixesActifs()
    const alertes = controlesCompta(commandes, plan, mvt)
    if (erreurMvt) {
      alertes.unshift({
        niveau: 'erreur',
        message: `Paiements et versements illisibles (${erreurMvt}) : encaissements, paies et reversements manquent au journal.`,
      })
    }
    return {
      ev,
      alertes,
      lignes,
      gl,
      kpi,
      mois: parMois(commandes, ev, plan),
      prefixes,
      resultat: valeursOhada(RESULTAT, gl, prefixes, kpi.resultat),
      actif: valeursOhada(BILAN_ACTIF, gl, prefixes, kpi.resultat),
      passif: valeursOhada(BILAN_PASSIF, gl, prefixes, kpi.resultat),
    }
  }, [commandes, plan, mvt, erreurMvt])

  if (erreur) return <p style={{ color: 'var(--danger)', padding: 18 }}>{erreur}</p>
  if (!plan || !compta) return <p style={{ padding: 18, color: 'var(--n400)' }}>Chargement…</p>

  if (plan.comptes.length === 0 || plan.types.length === 0) {
    /* Vide et qui le DIT. Un plan de secours écrit en dur donnerait une
       comptabilité d'apparence normale, calculée sur des comptes qui ne sont
       pas ceux de l'entreprise. */
    return (
      <div className="card">
        <div className="card-body">
          <b style={{ color: 'var(--danger)' }}>Plan comptable vide.</b>
          <p style={{ fontSize: 13.5 }}>
            Aucun compte ni type de transaction n'a été chargé depuis la base. Les états ne sont pas
            calculables — ils ne sont donc pas affichés, plutôt que d'être affichés à zéro.
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="subtabs">
        {VUES.map((v) => (
          <button
            key={v.cle}
            className={`subtab${vue === v.cle ? ' active' : ''}`}
            onClick={() => setVue(v.cle)}
          >
            <Icone nom={v.icone} />
            {v.libelle}
          </button>
        ))}
      </div>

      {vue === 'bord' && <Bord compta={compta} plan={plan} />}
      {vue === 'journal' && <Journal compta={compta} plan={plan} />}
      {vue === 'balance' && <Balance compta={compta} />}
      {vue === 'resultat' && <Etat postes={RESULTAT} valeurs={compta.resultat} titre="Compte de résultat — modèle officiel OHADA" />}
      {vue === 'bilan' && <Bilan compta={compta} />}
      {vue === 'flux' && (
        <Flux resultat={compta.resultat} bilan={{ ...compta.actif, ...compta.passif }} />
      )}
      {vue === 'comptes' && <PlanComptableVue plan={plan} gl={compta.gl} surRecharger={recharger} />}
      {vue === 'types' && <TypesEcritures plan={plan} surRecharger={recharger} />}
      {vue === 'manuel' && <EcrituresManuelles plan={plan} surAjout={recharger} />}
    </>
  )
}

/** Tout ce que le calcul comptable produit, en une seule structure. */
interface Compta {
  ev: EvenementComptable[]
  alertes: AlerteCompta[]
  lignes: LigneJournal[]
  gl: GrandLivre
  kpi: Indicateurs
  mois: MoisComptable[]
  prefixes: Record<string, string[]>
  resultat: Record<string, number>
  actif: Record<string, number>
  passif: Record<string, number>
}

function Bord({ compta, plan }: { compta: Compta; plan: PlanComptable }) {
  const { kpi, mois, ev } = compta
  const annuel = caAnnualise(mois)
  const fr = regimeFiscal(annuel)
  const marge = kpi.ca ? Math.round((kpi.resultat / kpi.ca) * 100) : 0

  const parType = (genre: string) =>
    plan.types
      .filter((t) => t.kind === genre)
      .map((t) => ({
        nom: t.name,
        montant: ev.filter((e) => e.tx === t.code).reduce((s, e) => s + e.montant, 0),
      }))
      .filter((x) => x.montant > 0)

  return (
    <>
      {compta.alertes.map((a, i) => (
        <p key={i} className={`avis ${a.niveau === 'erreur' ? 'avis-err' : 'avis-warn'}`} style={{ marginTop: 0, marginBottom: 10 }}>
          {a.message}
        </p>
      ))}
      <div className="stats">
        <Kpi l="GMV (volume traité)" v={fcfa(kpi.gmv)} />
        <Kpi l="CA net (modèle agent)" v={fcfa(kpi.ca)} c="var(--green)" />
        <Kpi l="Résultat" v={fcfa(kpi.resultat)} c={kpi.resultat >= 0 ? 'var(--green)' : 'var(--danger)'} />
        <Kpi l="Commandes livrées" v={String(kpi.nbCommandes)} />
        <Kpi l="Dette marchands (à reverser)" v={fcfa(kpi.detteMarchands)} c="var(--warning)" />
        <Kpi l="Dette livreurs (paie + pourboires)" v={fcfa(kpi.detteLivreurs)} c="var(--warning)" />
        <Kpi l="Avances clients (payé, non livré)" v={fcfa(kpi.avancesClients)} />
        {/* Aucune livraison sans paiement préalable : ce montant doit valoir 0.
            On ne l'affiche donc que comme ANOMALIE à corriger, quand il ne l'est pas. */}
        {kpi.creancesClients > 0 && (
          <Kpi l="Anomalie : livré sans paiement enregistré" v={fcfa(kpi.creancesClients)} c="var(--danger)" />
        )}
        <Kpi l="Float mobile money" v={fcfa(kpi.floatMobileMoney)} c="var(--accent)" />
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 12, color: 'var(--n400)', textTransform: 'uppercase', letterSpacing: '.4px' }}>
              Régime fiscal (Mali · DGI)
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: `var(${fr.couleur})` }}>{fr.regime}</div>
            <div style={{ fontSize: 12, color: 'var(--n400)' }}>{fr.precision}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 12, color: 'var(--n400)' }}>CA net annualisé (projection)</div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{fcfa(annuel)}</div>
            <span className="badge">{fr.tva}</span>
          </div>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--n400)', padding: '0 16px 12px' }}>
          {/* Le seuil se lit sur le CA réel. Sur le GMV, l'entreprise basculerait
              au réel normal et déclarerait une TVA sur un CA qu'elle ne fait pas. */}
          Le seuil TVA se calcule sur le CA réel (commissions + frais), pas sur le GMV. Marge nette
          actuelle : <b style={{ color: `var(${kpi.resultat >= 0 ? '--green' : '--danger'})` }}>{marge} %</b>.
        </div>
      </div>

      <CarteTVA compta={compta} plan={plan} annuel={annuel} />

      {/* QUATRE graphiques, comme l'original. Celui des lignes manquait
          entièrement : c'est le seul qui superpose GMV, CA net et charges sur
          le même axe, donc le seul qui montre l'écart entre le volume traité
          et ce qu'on gagne. En modèle agent, c'est LA courbe à regarder. */}
      <div className="chart-grid">
        <div className="card chart-card">
          <div className="chart-h">GMV / CA / Charges dans le temps</div>
          <GraphiqueLignes mois={mois} />
          <LegendeLignes />
        </div>
        <div className="card chart-card">
          <div className="chart-h">CA par type de produit</div>
          <Anneau
            parts={parType('revenue').map((l, i) => ({
              libelle: l.nom,
              valeur: l.montant,
              couleur: COULEURS[i % COULEURS.length]!,
            }))}
          />
        </div>
        <Repartition titre="Charges par catégorie" lignes={parType('expense')} />
        <ResultatMensuel mois={mois} />
      </div>

      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-header">Par mois</div>
        <div className="card-body">
          <table>
            <thead><tr><th>Mois</th><th style={{ textAlign: 'right' }}>GMV</th><th style={{ textAlign: 'right' }}>CA net</th><th style={{ textAlign: 'right' }}>Charges</th><th style={{ textAlign: 'right' }}>Résultat</th></tr></thead>
            <tbody>
              {mois.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--n400)', padding: 16 }}>Aucune donnée.</td></tr>
              )}
              {mois.map((m) => (
                <tr key={m.mois}>
                  <td>{m.mois}</td>
                  <td style={{ textAlign: 'right', color: 'var(--n400)' }}>{fcfa(m.gmv)}</td>
                  <td style={{ textAlign: 'right' }}>{fcfa(m.ca)}</td>
                  <td style={{ textAlign: 'right' }}>{fcfa(m.charges)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: `var(${m.ca - m.charges >= 0 ? '--green' : '--danger'})` }}>
                    {fcfa(m.ca - m.charges)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p style={{ fontSize: 11.5, color: 'var(--n400)', marginTop: 10 }}>
        Modèle agent SYSCOHADA : le CA = commission + frais de livraison + produits accessoires +
        Premium, jamais le GMV — l'argent des articles transite par le compte 401. Montants en FCFA.
      </p>
    </>
  )
}

const Kpi = ({ l, v, c }: { l: string; v: string; c?: string }) => (
  <div className="stat">
    <div className="label">{l}</div>
    <div className="val" style={c ? { color: c } : undefined}><Compteur valeur={String(v)} /></div>
  </div>
)

const COULEURS = ['--green', '--accent', '--warning', '--ink', '--danger']

function Repartition({ titre, lignes }: { titre: string; lignes: Array<{ nom: string; montant: number }> }) {
  const max = Math.max(1, ...lignes.map((l) => l.montant))
  return (
    <div className="card chart-card">
      <div className="chart-h">{titre}</div>
      {lignes.length === 0 && <div className="ch-empty">—</div>}
      {lignes.map((l, i) => (
        <div className="hbar" key={l.nom}>
          <div className="hbar-l">{l.nom}</div>
          <div className="hbar-t">
            <div
              className="hbar-f"
              style={{ width: `${((l.montant / max) * 100).toFixed(1)}%`, background: `var(${COULEURS[i % COULEURS.length]})` }}
            />
          </div>
          <div className="hbar-v">{fcfa(l.montant)}</div>
        </div>
      ))}
    </div>
  )
}

/**
 * Résultat net mensuel, en barres verticales.
 *
 * Le signe pilote la couleur : un mois déficitaire doit se voir sans lire le
 * chiffre. `.bars` / `.bar-col` / `.bar-wrap` / `.bar-b` / `.bar-x` portent
 * toute la géométrie.
 */
function ResultatMensuel({ mois }: { mois: MoisComptable[] }) {
  if (mois.length === 0) {
    return (
      <div className="card chart-card">
        <div className="chart-h">Résultat net mensuel</div>
        <div className="ch-empty">—</div>
      </div>
    )
  }
  const max = Math.max(1, ...mois.map((m) => Math.abs(m.ca - m.charges)))
  return (
    <div className="card chart-card">
      <div className="chart-h">Résultat net mensuel</div>
      <div className="bars">
        {mois.map((m) => {
          const v = m.ca - m.charges
          const h = Math.max(3, (Math.abs(v) / max) * 70)
          return (
            <div className="bar-col" key={m.mois}>
              <div className="bar-wrap">
                <div
                  className="bar-b"
                  title={fcfa(v)}
                  style={{ height: `${h.toFixed(0)}px`, background: `var(${v >= 0 ? '--green' : '--danger'})` }}
                />
              </div>
              <div className="bar-x">
                {m.mois.slice(5)}/{m.mois.slice(2, 4)}
              </div>
            </div>
          )
        })}
      </div>
      <div className="chart-legend">
        <span className="ch-leg"><span style={{ background: 'var(--green)' }} />Bénéfice</span>
        <span className="ch-leg"><span style={{ background: 'var(--danger)' }} />Perte</span>
      </div>
    </div>
  )
}

function Journal({ compta, plan }: { compta: Compta; plan: PlanComptable }) {
  const [type, setType] = useState('')
  const [du, setDu] = useState('')
  const [au, setAu] = useState('')

  const parCode = new Map(plan.types.map((t) => [t.code, t]))
  const libelleCompte = (code: string) =>
    plan.comptes.find((c) => c.code === code)?.label ?? code

  const visibles = compta.ev
    .filter((e) => (!type || e.tx === type) && (!du || e.date >= du) && (!au || e.date <= au))
    .sort((a, b) => b.date.localeCompare(a.date))
  const total = visibles.reduce((s, e) => s + e.montant, 0)

  return (
    <div className="card">
      <div className="card-header" style={{ gap: 10, flexWrap: 'wrap' }}>
        <ListeDeroulante
          valeur={type}
          surChangement={setType}
          options={[
            { valeur: '', libelle: 'Tous les types' },
            ...plan.types.map((t) => ({ valeur: t.code, libelle: t.name })),
          ]}
        />
        <input type="date" value={du} onChange={(e) => setDu(e.target.value)} />
        <input type="date" value={au} onChange={(e) => setAu(e.target.value)} />
      </div>
      <div className="card-body flush">
        <table>
          <thead>
            <tr><th>Date</th><th>Type</th><th>Compte débité</th><th>Compte crédité</th><th style={{ textAlign: 'right' }}>Montant</th><th>Libellé</th></tr>
          </thead>
          <tbody>
            {visibles.length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--n400)', padding: 20 }}>Aucune écriture.</td></tr>
            )}
            {visibles.map((e, i) => {
              const t = parCode.get(e.tx)
              return (
                <tr key={`${e.tx}-${e.date}-${i}`}>
                  <td>{e.date}</td>
                  <td><span className="jrn-pill">{e.tx}</span></td>
                  <td><span className="acc-code">{t?.debit ?? '—'}</span> {t ? libelleCompte(t.debit) : ''}</td>
                  <td><span className="acc-code">{t?.credit ?? '—'}</span> {t ? libelleCompte(t.credit) : ''}</td>
                  <td className="num-d" style={{ textAlign: 'right' }}>{fcfa(e.montant)}</td>
                  <td>{e.libelle}</td>
                </tr>
              )
            })}
            <tr style={{ background: 'var(--cream)' }}>
              <td colSpan={4} style={{ textAlign: 'right', fontWeight: 700 }}>Total mouvementé (Débit = Crédit)</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{fcfa(total)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * La TVA, telle qu'elle sera déclarée.
 *
 * Deux comptes et une soustraction : 443 (facturée à nos clients) moins 445
 * (récupérable sur nos achats). Ce n'est pas un indicateur de gestion — c'est
 * le montant à verser à l'État, ou le crédit reportable si le second dépasse
 * le premier. Il n'apparaissait nulle part avant la migration 0039.
 *
 * ⚠️ La carte affiche aussi une CONTRADICTION quand elle existe. Le régime
 * fiscal affiché plus haut est une PROJECTION calculée sur le chiffre
 * d'affaires ; l'assujettissement, lui, est un réglage DÉCLARÉ. Les deux
 * peuvent diverger — et cette divergence est exactement le moment où
 * l'entreprise doit se manifester auprès de la DGI. Laisser les deux chiffres
 * côte à côte sans rien dire reviendrait à la laisser passer.
 */
function CarteTVA({
  compta,
  plan,
  annuel,
}: {
  compta: Compta
  plan: PlanComptable
  annuel: number
}) {
  const solde = (code: string, sens: 'credit' | 'debit') => {
    const c = compta.gl[code]
    if (!c) return 0
    return Math.max(0, sens === 'credit' ? c.credit - c.debit : c.debit - c.credit)
  }
  // 443 est un compte de PASSIF (ce qu'on doit), 445 un compte d'ACTIF (ce
  // qu'on récupère) : leurs soldes se lisent de deux côtés opposés.
  const facturee = solde('443', 'credit')
  const recuperable = solde('445', 'debit')
  const due = facturee - recuperable

  const seuilDepasse = annuel >= 100e6
  const contradiction = seuilDepasse && !plan.tva.actif

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div className="card-header">
        TVA
        <span style={{ color: 'var(--n400)', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
          {plan.tva.actif
            ? `assujettie — taux ${(plan.tva.taux * 100).toFixed(plan.tva.taux * 100 % 1 ? 1 : 0)} %`
            : 'non assujettie — aucune taxe n’est calculée'}
        </span>
      </div>
      <div className="card-body">
        <div className="rl">
          <span style={{ flex: 1 }}>TVA facturée sur nos produits (443)</span>
          <b>{facturee ? fcfa(facturee) : '—'}</b>
        </div>
        <div className="rl">
          <span style={{ flex: 1 }}>TVA récupérable sur achats (445)</span>
          <b>{recuperable ? fcfa(recuperable) : '—'}</b>
        </div>
        <div className="rl rl-tot" style={{ background: 'var(--cream)' }}>
          <span style={{ flex: 1, fontWeight: 700, textTransform: 'uppercase', fontSize: 12.5 }}>
            {due >= 0 ? 'TVA due à l’État' : 'Crédit de TVA reportable'}
          </span>
          <b style={{ color: `var(${due > 0 ? '--warning' : '--green'})` }}>
            {due ? fcfa(Math.abs(due)) : '—'}
          </b>
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--n400)', padding: '0 16px 12px', lineHeight: 1.5 }}>
        {contradiction ? (
          <b style={{ color: 'var(--danger)' }}>
            Le CA annualisé dépasse 100 M FCFA — la TVA est applicable au réel normal — alors que
            l’entreprise est réglée comme non assujettie. À arbitrer avec l’expert-comptable : tant
            que le réglage reste à « Non », aucune taxe n’est ni facturée ni provisionnée.
          </b>
        ) : plan.tva.actif ? (
          <>
            La taxe est extraite des montants facturés, traités comme TTC. Le chiffre d’affaires
            affiché est donc <b>hors taxes</b> : c’est la règle, et c’est aussi ce qui explique
            qu’il ait baissé le jour de l’assujettissement.
          </>
        ) : (
          <>
            Aucune TVA n’est calculée. Les comptes 443 et 445 existent au plan mais restent vides —
            à activer dans Config le jour de l’assujettissement.
          </>
        )}
      </div>
    </div>
  )
}

function Balance({ compta }: { compta: Compta }) {
  /* Cliquer un compte ouvre son GRAND-LIVRE : le détail ligne à ligne avec le
     solde progressif. Sans lui, la balance donne un total sans jamais dire
     d'où il vient — et un écart devient introuvable. */
  const [compteOuvert, setCompteOuvert] = useState<string | null>(null)

  const lignes = Object.values(compta.gl)
    .filter((a) => a.debit || a.credit)
    .sort((a, b) => a.code.localeCompare(b.code))
  const td = lignes.reduce((s, a) => s + a.debit, 0)
  const tc = lignes.reduce((s, a) => s + a.credit, 0)

  return (
    <div className="card">
      {/* L'indication est dans l'original, et elle n'est pas décorative : rien
          d'autre ne signale qu'une ligne est cliquable. Sans elle, le
          grand-livre existe sans que personne ne le trouve. */}
      <div className="card-header">
        Balance des comptes
        <span style={{ color: 'var(--n400)', fontWeight: 400, fontSize: 12, marginLeft: 8 }}>
          clic sur un compte → grand-livre (entrées / sorties)
        </span>
      </div>
      <div className="card-body flush">
        <table>
          <thead>
            {/* « (entrées) » et « (sorties) » : sans cette précision, un
                dispatcher qui n'est pas comptable lit « Débit » et comprend
                l'inverse de ce que la colonne contient. */}
            <tr>
              <th>Compte</th>
              <th>Libellé</th>
              <th style={{ textAlign: 'right' }}>Débit (entrées)</th>
              <th style={{ textAlign: 'right' }}>Crédit (sorties)</th>
              <th style={{ textAlign: 'right' }}>Solde</th>
            </tr>
          </thead>
          <tbody>
            {lignes.map((a) => (
              <tr key={a.code} style={{ cursor: 'pointer' }} onClick={() => setCompteOuvert(a.code)}>
                <td><span className="acc-code">{a.code}</span></td>
                <td>{a.label}</td>
                <td className="num-d" style={{ textAlign: 'right' }}>{fcfa(a.debit)}</td>
                <td className="num-c" style={{ textAlign: 'right' }}>{fcfa(a.credit)}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>
                  {fcfa(Math.abs(a.solde))} {a.solde >= 0 ? 'D' : 'C'}
                </td>
              </tr>
            ))}
            {/* Le contrôle qui compte : en partie double, la somme des débits
                DOIT égaler celle des crédits. Un écart signale une écriture
                incomplète, pas une erreur d'affichage. */}
            <tr style={{ background: 'var(--cream)', fontWeight: 700 }}>
              <td colSpan={2} style={{ textAlign: 'right' }}>Totaux</td>
              <td style={{ textAlign: 'right' }}>{fcfa(td)}</td>
              <td style={{ textAlign: 'right' }}>{fcfa(tc)}</td>
              <td style={{ textAlign: 'right', color: `var(${Math.abs(td - tc) < 1 ? '--green' : '--danger'})` }}>
                {Math.abs(td - tc) < 1 ? '✔ équilibrée' : `écart ${fcfa(Math.abs(td - tc))}`}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <GrandLivreCompte
        code={compteOuvert}
        lignes={compta.lignes}
        libelle={compteOuvert ? (compta.gl[compteOuvert]?.label ?? '') : ''}
        surFermer={() => setCompteOuvert(null)}
      />
    </div>
  )
}

function Etat({ postes, valeurs, titre }: { postes: PosteOhada[]; valeurs: Record<string, number>; titre: string }) {
  return (
    <div className="card">
      <div className="card-header">{titre}</div>
      <div className="card-body">
        {postes.map((r) => {
          const v = valeurs[r.ref] ?? 0
          const totalisant = Boolean(r.f || r.grand || r.bal)
          return (
            /* ⚠️ `.rl` et `.rl-tot`, PAS des styles en ligne. Ces deux classes
               portent la mise en forme des états OHADA : filet pointillé entre
               les postes, trait plein et gras au-dessus des totalisants, et
               surtout `font-variant-numeric: tabular-nums` sur les montants —
               sans quoi les chiffres ne s'alignent pas en colonne et un état
               comptable devient pénible à relire. */
            <div
              key={r.ref}
              className={totalisant ? 'rl rl-tot' : 'rl'}
              style={r.net || r.total ? { background: 'var(--cream)' } : undefined}
            >
              <span className="acc-code" style={{ minWidth: 34, textAlign: 'center' }}>{r.ref}</span>
              {r.sign && (
                <span style={{ width: 14, textAlign: 'center', color: `var(${r.sign === '+' ? '--green' : '--danger'})`, fontWeight: 700 }}>
                  {r.sign}
                </span>
              )}
              <span style={{ flex: 1, fontWeight: totalisant ? 700 : 400, textTransform: totalisant ? 'uppercase' : undefined, fontSize: totalisant ? 12.5 : 13 }}>
                {r.label}
              </span>
              <b style={{ color: totalisant ? `var(${v >= 0 ? '--green' : '--danger'})` : undefined }}>
                {v ? fcfa(v) : '—'}
              </b>
            </div>
          )
        })}
      </div>
      <div className="card-body" style={{ borderTop: '1px solid var(--n200)', fontSize: 11.5, color: 'var(--n400)' }}>
        Codes REF et cascade conformes OHADA. L'état légal annuel est établi et visé par
        l'expert-comptable — celui-ci en est la préparation, pas le substitut.
      </div>
    </div>
  )
}

function Bilan({ compta }: { compta: Compta }) {
  const totalActif = compta.actif['BZ'] ?? 0
  const totalPassif = compta.passif['DZ'] ?? 0
  const ecart = totalActif - totalPassif

  return (
    <>
      <BilanActif valeurs={compta.actif} />
      <div style={{ height: 14 }} />
      <Etat postes={BILAN_PASSIF} valeurs={compta.passif} titre="Bilan — PASSIF (OHADA)" />
      <div className="card" style={{ marginTop: 14 }}>
        <div className="card-body" style={{ fontSize: 13 }}>
          {/* Le contrôle d'équilibre est la seule chose qui distingue un bilan
              juste d'un bilan qui a l'air juste. Il est affiché, pas caché. */}
          {Math.abs(ecart) < 1 ? (
            <b style={{ color: 'var(--green)' }}>
              ✔ Bilan équilibré — Total Actif = Total Passif = {fcfa(totalActif)}
            </b>
          ) : (
            <b style={{ color: 'var(--danger)' }}>
              Écart d'équilibre : {fcfa(Math.abs(ecart))} — vérifier le mapping des comptes.
            </b>
          )}
          <p style={{ margin: '6px 0 0', color: 'var(--n400)', fontSize: 12 }}>
            Le résultat net est injecté dans les capitaux propres. Les amortissements (comptes 28/29)
            sont nuls dans le modèle agent : l'actif net égale l'actif brut.
          </p>
        </div>
      </div>
    </>
  )
}

/**
 * Grand-livre d'un compte — TRANSPOSITION de `openGrandLivre`.
 *
 * Le détail ligne à ligne, avec le SOLDE PROGRESSIF. C'est ce qui manquait :
 * la balance donnait un total sans jamais dire d'où il venait, et un écart
 * n'était donc pas traçable.
 */
function GrandLivreCompte({
  code,
  libelle,
  lignes,
  surFermer,
}: {
  code: string | null
  libelle: string
  lignes: LigneJournal[]
  surFermer: () => void
}) {
  if (!code) return <Modale titre="" ouverte={false} surFermer={surFermer}><span /></Modale>

  const detail = lignes
    .filter((l) => l.compte === code)
    .slice()
    .sort((a, b) => (a.date || '').localeCompare(b.date || ''))

  let solde = 0
  const avecSolde = detail.map((l) => {
    const d = l.sens === 'debit' ? l.montant : 0
    const c = l.sens === 'credit' ? l.montant : 0
    solde += d - c
    return { ...l, d, c, solde }
  })
  const td = avecSolde.reduce((s, l) => s + l.d, 0)
  const tc = avecSolde.reduce((s, l) => s + l.c, 0)

  return (
    <Modale titre={`Grand-livre — ${code} ${libelle}`} ouverte surFermer={surFermer}>
      <div style={{ maxHeight: '56vh', overflow: 'auto' }}>
        <table style={{ width: '100%', fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Date</th>
              <th style={{ textAlign: 'left' }}>Libellé</th>
              <th style={{ textAlign: 'right' }}>Entrées (D)</th>
              <th style={{ textAlign: 'right' }}>Sorties (C)</th>
              <th style={{ textAlign: 'right' }}>Solde</th>
            </tr>
          </thead>
          <tbody>
            {avecSolde.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', color: 'var(--n400)', padding: 18 }}>
                  Aucun mouvement sur ce compte.
                </td>
              </tr>
            )}
            {avecSolde.map((l, i) => (
              <tr key={i}>
                <td>{l.date}</td>
                <td>{l.libelle}</td>
                <td className="num-d" style={{ textAlign: 'right' }}>{l.d ? fcfa(l.d) : ''}</td>
                <td className="num-c" style={{ textAlign: 'right' }}>{l.c ? fcfa(l.c) : ''}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{fcfa(l.solde)}</td>
              </tr>
            ))}
            <tr style={{ background: 'var(--cream)' }}>
              <td colSpan={2} style={{ textAlign: 'right', fontWeight: 700 }}>Totaux</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{fcfa(td)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{fcfa(tc)}</td>
              <td style={{ textAlign: 'right', fontWeight: 700 }}>{fcfa(td - tc)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </Modale>
  )
}
